import { ObjectBuilder, GridUtils, Image3D, ImgButton, MeshUtils, Rectangle3D, ScrollViewer3D } from "@dreamcam/babylon-tools";
import { MeshName } from "../MeshName";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { Env } from "../../settings/Env";
import { AppView } from "../AppView";
import { ScrollHandler } from "../ScrollHandler";
import { createVector3 } from "../ButtonAnimation";
import { XRInputHelper } from "../XRInputHelper";
import { text } from "../../TextResources";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { EnvironmentController } from "../../settings/EnvironmentController";
export class EnvironmentDialog {
    constructor(callbacks) {
        this._environmentData = null;
        this._onHideCallback = null;
        this.show = (environmentData, onHideCallback) => {
            this._environmentData = environmentData;
            this._onHideCallback = onHideCallback;
            this._fillEnvironmentList(this._environmentData, this._environmentsContainer);
            MeshUtils.show(this._mesh2D);
            this._scrollHandler.addThumbstickAxesChangedListener();
            AppView.getInstance().scene.getEngine().onEndFrameObservable.add(this._onEndFrameHandler);
        };
        this.hide = () => {
            if (MeshUtils.isHidden(this._mesh2D))
                return;
            AppView.getInstance().scene.getEngine().onEndFrameObservable.removeCallback(this._onEndFrameHandler);
            this._environmentData = null;
            MeshUtils.hide(this._mesh2D);
            this._scrollHandler.releaseThumbstickAxesChangedListener();
            if (this._onHideCallback) {
                this._onHideCallback();
                this._onHideCallback = null;
            }
        };
        this._loadingMarks = [];
        this._currentEnvironmentControls = undefined;
        this._changingEnvironment = false;
        this._onEndFrameHandler = () => {
            const millis = new Date().getTime();
            if (this._loadingMarkRotationStartMillis === undefined) {
                this._loadingMarkRotationStartMillis = millis;
            }
            for (let image of this._loadingMarks) {
                image.rotation = (millis - this._loadingMarkRotationStartMillis) * 2 * Math.PI / Env.lobby.environmentDialog.environmentItem.loadingMark.periodMillis;
            }
        };
        this._callbacks = callbacks;
        const appView = AppView.getInstance();
        const ed = Env.lobby.environmentDialog;
        this._mesh2D = MeshUtils.createPlaneMesh(MeshName.environmentForm, ed.background.width, ed.background.height, appView.scene, appView.guiRootNode, DcRenderingGroup.dialogs);
        this._mesh2D.mesh.position = createVector3(Env.lobby.environmentDialog.position);
        this._scrollHandler = new ScrollHandler([MeshName.environmentForm], this._mesh2D.m2px(ed.scrollView.speed), v => {
            const innerHeight = this._scrollViewer.children[0].heightInPixels;
            const outerHeight = this._scrollViewer.heightInPixels;
            const clamp01 = (value) => Math.max(Math.min(value, 1), 0);
            this._scrollViewer.verticalValue =
                clamp01(this._scrollViewer.verticalValue + v / (innerHeight - outerHeight));
        });
        const pointerAddon = XRInputHelper.addPointerAddon(this._mesh2D.mesh);
        pointerAddon.onPointerOut.add(() => {
            this._scrollHandler.stopScroll();
            this._mesh2D.notifyChildrenOnPointerOut();
        });
        const container = new Rectangle3D("environmentDialogRootContainer");
        container.cornerRadius = this._mesh2D.m2px(ed.background.cornerRadius);
        container.background = ed.background.backgroundColor;
        container.thickness = 0;
        this._mesh2D.texture.addControl(container);
        const mainGrid = GridUtils.CreateGrid("environmentDialogMainGrid", ed.mainGrid, this._mesh2D);
        container.addControl(mainGrid);
        const headerGrid = GridUtils.CreateGrid("environmentDialogHeaderGrid", ed.headerGrid, this._mesh2D);
        headerGrid.background = ed.headerGrid.background;
        mainGrid.addControl(headerGrid);
        const headerText = ObjectBuilder.createTextBlock3D(this._mesh2D, "environmentDialogHeaderText", text.environmentDialog_header, ed.header);
        headerGrid.addControl(headerText, 0, 0);
        const closeButton = new ImgButton("environmentDialogCloseButton", ed.close);
        closeButton.widthInPixels = this._mesh2D.m2px(ed.close.size + ed.close.padding);
        closeButton.heightInPixels = this._mesh2D.m2px(ed.close.size + ed.close.padding);
        closeButton.paddingTopInPixels = this._mesh2D.m2px(ed.close.padding);
        closeButton.paddingRightInPixels = this._mesh2D.m2px(ed.close.padding);
        closeButton.paddingBottomInPixels = this._mesh2D.m2px(ed.close.padding);
        closeButton.onPointerClickObservable.add(this.hide);
        headerGrid.addControl(closeButton, 0, 1);
        this._scrollViewer = new ScrollViewer3D("environmentDialogScrollViewer");
        this._scrollViewer.heightInPixels = this._mesh2D.m2px(ed.background.height - ed.headerGrid.height);
        this._scrollViewer.thickness = 0;
        this._scrollViewer.color = ed.scrollView.colors.background;
        this._scrollViewer.barColor = ed.scrollView.colors.bar;
        this._scrollViewer.hoverBarColor = ed.scrollView.colors.hoverBar;
        this._scrollViewer.pressedBarColor = ed.scrollView.colors.pressedBar;
        this._scrollViewer.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        this._scrollViewer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        this._scrollViewer.verticalBarPaddingInPixels = {
            left: this._mesh2D.m2px(ed.scrollView.verticalBar.padding.left),
            right: this._mesh2D.m2px(ed.scrollView.verticalBar.padding.right),
            top: this._mesh2D.m2px(ed.scrollView.verticalBar.padding.top),
            bottom: this._mesh2D.m2px(ed.scrollView.verticalBar.padding.bottom)
        };
        this._scrollViewer.barSizeInPixels = this._mesh2D.m2px(ed.scrollView.verticalBar.size);
        this._scrollViewer.forceVerticalBar = undefined;
        this._scrollViewer.horizontalBar.isVisible = false;
        this._scrollViewer.verticalBar.isVisible = true;
        this._scrollViewer.verticalValue = 0;
        mainGrid.addControl(this._scrollViewer, 1);
        this._environmentsContainer = new Container("environmentDialogList");
        this._environmentsContainer.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
        this._environmentsContainer.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        this._environmentsContainer.widthInPixels = this._mesh2D.m2px(ed.background.width);
        this._environmentsContainer.heightInPixels = this._mesh2D.m2px(ed.environmentItem.height);
        this._scrollViewer.addControl(this._environmentsContainer);
    }
    dispose() {
        this._mesh2D.dispose();
    }
    async _preloadEnvironmentAsync(sceneEnvironment) {
        return await AppView.getInstance().environmentView.preloadEnvironmentAsync(sceneEnvironment);
    }
    async _setEnvironmentAsync(sceneEnvironment) {
        return await AppView.getInstance().environmentView.updateEnvironmentAsync(sceneEnvironment);
    }
    _fillEnvironmentList(data, parent) {
        parent.clearControls();
        this._loadingMarks = [];
        if (data === null)
            return;
        this._currentEnvironmentControls = {
            descriptor: EnvironmentController.getCurrentDescriptor(data),
        };
        // Log.debug("Count:", data.allDescriptors.length);
        const it = Env.lobby.environmentDialog.environmentItem;
        for (let i = 0; i < data.allDescriptors.length; i++) {
            const row = Math.floor(i / 2);
            const col = i % 2;
            const descriptor = data.allDescriptors[i];
            const isCurrentlyActiveDescriptor = descriptor.code === this._currentEnvironmentControls.descriptor.code;
            // Parent container for all descriptor's controls.
            const container = new Container(`container_${col}x${row}`);
            container["descriptor"] = descriptor;
            container.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
            container.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
            container.widthInPixels = this._mesh2D.m2px(it.width);
            container.heightInPixels = this._mesh2D.m2px(it.height);
            // outlineRectangle has thickness on currently active environment.
            const outlineRectangle = new Rectangle3D(`rectangle_${col}x${row}`);
            outlineRectangle.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
            outlineRectangle.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
            outlineRectangle.widthInPixels = this._mesh2D.m2px(it.outlineRectangle.width);
            outlineRectangle.heightInPixels = this._mesh2D.m2px(it.outlineRectangle.height);
            outlineRectangle.leftInPixels = this._mesh2D.m2px(col === 0 ? it.outlineRectangle.left0 : it.outlineRectangle.left1);
            outlineRectangle.topInPixels = this._mesh2D.m2px(it.outlineRectangle.top);
            outlineRectangle.cornerRadius = this._mesh2D.m2px(it.outlineRectangle.cornerRadius);
            outlineRectangle.thickness = isCurrentlyActiveDescriptor
                ? outlineRectangle.thickness = this._mesh2D.m2px(it.outlineRectangle.activeThickness)
                : 0;
            outlineRectangle.color = it.outlineRectangle.hoverColor;
            container.addControl(outlineRectangle);
            const image = new Image3D(`image_${col}x${row}`, descriptor.icon);
            outlineRectangle.addControl(image);
            // hoverOverlayRectangle makes environment darker on hover.
            const hoverOverlayRectangle = new Rectangle3D(`activeOverlayRectangle_${col}x${row}`);
            hoverOverlayRectangle.cornerRadius = outlineRectangle.cornerRadius;
            hoverOverlayRectangle.thickness = 0;
            hoverOverlayRectangle.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
            hoverOverlayRectangle.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
            hoverOverlayRectangle.leftInPixels = outlineRectangle.leftInPixels;
            hoverOverlayRectangle.topInPixels = outlineRectangle.topInPixels;
            hoverOverlayRectangle.widthInPixels = outlineRectangle.widthInPixels;
            hoverOverlayRectangle.heightInPixels = outlineRectangle.heightInPixels;
            hoverOverlayRectangle.background = it.overlayActiveRectangle.background;
            hoverOverlayRectangle.zIndex = 10;
            hoverOverlayRectangle.isVisible = false;
            container.addControl(hoverOverlayRectangle);
            // currentEnvironmentMark marks currently active environment with a checkbox icon.
            const currentEnvironmentMark = new Image3D(`currentEnvironmentMark_${col}x${row}`, it.currentEnvironmentMark.img);
            currentEnvironmentMark.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
            currentEnvironmentMark.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
            currentEnvironmentMark.widthInPixels = this._mesh2D.m2px(it.currentEnvironmentMark.width);
            currentEnvironmentMark.heightInPixels = this._mesh2D.m2px(it.currentEnvironmentMark.height);
            currentEnvironmentMark.leftInPixels = hoverOverlayRectangle.leftInPixels + (hoverOverlayRectangle.widthInPixels - currentEnvironmentMark.widthInPixels) / 2;
            currentEnvironmentMark.topInPixels = hoverOverlayRectangle.topInPixels + (hoverOverlayRectangle.heightInPixels - currentEnvironmentMark.heightInPixels) / 2;
            currentEnvironmentMark.zIndex = 20;
            currentEnvironmentMark.isVisible = isCurrentlyActiveDescriptor;
            container.addControl(currentEnvironmentMark);
            // loadableEnvironmentMark on hover marks currently inactive environment with a downloadable icon.
            const loadableEnvironmentMark = new Image3D(`currentEnvironmentMark_${col}x${row}`, it.loadableEnvironmentMark.img);
            loadableEnvironmentMark.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
            loadableEnvironmentMark.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
            loadableEnvironmentMark.widthInPixels = this._mesh2D.m2px(it.currentEnvironmentMark.width);
            loadableEnvironmentMark.heightInPixels = this._mesh2D.m2px(it.currentEnvironmentMark.height);
            loadableEnvironmentMark.leftInPixels = hoverOverlayRectangle.leftInPixels + (hoverOverlayRectangle.widthInPixels - loadableEnvironmentMark.widthInPixels) / 2;
            loadableEnvironmentMark.topInPixels = hoverOverlayRectangle.topInPixels + (hoverOverlayRectangle.heightInPixels - loadableEnvironmentMark.heightInPixels) / 2;
            loadableEnvironmentMark.zIndex = 20;
            loadableEnvironmentMark.isVisible = false;
            container.addControl(loadableEnvironmentMark);
            if (isCurrentlyActiveDescriptor) {
                this._currentEnvironmentControls = {
                    descriptor,
                    outlineRectangle,
                    currentEnvironmentMark,
                };
            }
            // isNewMark highlights new environments.
            const isNewMark = new Image3D(`isNewMark_${col}x${row}`, it.isNewMark.img);
            isNewMark.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
            isNewMark.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
            isNewMark.widthInPixels = this._mesh2D.m2px(it.isNewMark.width);
            isNewMark.heightInPixels = this._mesh2D.m2px(it.isNewMark.height);
            isNewMark.leftInPixels = hoverOverlayRectangle.leftInPixels + hoverOverlayRectangle.widthInPixels - isNewMark.widthInPixels - this._mesh2D.m2px(it.isNewMark.right);
            isNewMark.topInPixels = hoverOverlayRectangle.topInPixels + this._mesh2D.m2px(it.isNewMark.top);
            isNewMark.zIndex = 20;
            isNewMark.isVisible = descriptor.isNew === true;
            container.addControl(isNewMark);
            // loadingMark - an icon to brighten up the environment loading time.
            const loadingMark = new Image3D(`loadingMark_${col}x${row}`, it.loadingMark.img);
            loadingMark.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
            loadingMark.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
            loadingMark.widthInPixels = this._mesh2D.m2px(it.loadingMark.width);
            loadingMark.heightInPixels = this._mesh2D.m2px(it.loadingMark.height);
            loadingMark.leftInPixels = hoverOverlayRectangle.leftInPixels + (hoverOverlayRectangle.widthInPixels - loadingMark.widthInPixels) / 2;
            loadingMark.topInPixels = hoverOverlayRectangle.topInPixels + (hoverOverlayRectangle.heightInPixels - loadingMark.heightInPixels) / 2;
            loadingMark.zIndex = 20;
            loadingMark.isVisible = false;
            container.addControl(loadingMark);
            this._loadingMarks.push(loadingMark);
            // text - environment name.
            const text = ObjectBuilder.createTextBlock3D(this._mesh2D, `text_${col}x${row}`, descriptor.name, it.caption);
            container.addControl(text);
            if (row > 0 && col === 0) {
                parent.heightInPixels = (row + 1) * this._mesh2D.m2px(it.height);
            }
            container.leftInPixels = col === 0 ? 0 : this._mesh2D.m2px(it.width);
            container.topInPixels = row * this._mesh2D.m2px(it.height);
            parent.addControl(container);
            container.onPointerEnterObservable.add((control) => {
                hoverOverlayRectangle.isVisible = true;
                const descriptor = control["descriptor"];
                loadableEnvironmentMark.isVisible =
                    descriptor !== undefined &&
                        this._currentEnvironmentControls !== undefined &&
                        descriptor.code !== this._currentEnvironmentControls.descriptor.code;
            });
            container.onPointerOutObservable.add(() => {
                hoverOverlayRectangle.isVisible = false;
                loadableEnvironmentMark.isVisible = false;
            });
            container.onPointerClickObservable.add(async (eventData, eventState) => {
                if (eventData.buttonIndex !== 0)
                    return;
                if (this._changingEnvironment)
                    return;
                const descriptor = eventState.currentTarget["descriptor"];
                if (descriptor === undefined || this._currentEnvironmentControls === undefined || descriptor.code === this._currentEnvironmentControls.descriptor.code)
                    return;
                try {
                    this._changingEnvironment = true;
                    loadableEnvironmentMark.isVisible = false;
                    loadingMark.isVisible = true;
                    const isPreloaded = await this._preloadEnvironmentAsync(descriptor.environment);
                    if (isPreloaded) {
                        await AppView.getInstance().fadingHelper?.fadeOutAsync(false);
                        try {
                            const isUpdated = await this._setEnvironmentAsync(descriptor.environment);
                            if (isUpdated) {
                                const changeCurrentEnvironmentControls = () => {
                                    if (this._currentEnvironmentControls && this._currentEnvironmentControls.outlineRectangle && this._currentEnvironmentControls.currentEnvironmentMark) {
                                        this._currentEnvironmentControls.outlineRectangle.thickness = 0;
                                        this._currentEnvironmentControls.currentEnvironmentMark.isVisible = false;
                                    }
                                    outlineRectangle.thickness = this._mesh2D.m2px(it.outlineRectangle.activeThickness);
                                    currentEnvironmentMark.isVisible = true;
                                    this._currentEnvironmentControls = {
                                        descriptor,
                                        outlineRectangle,
                                        currentEnvironmentMark
                                    };
                                };
                                changeCurrentEnvironmentControls();
                                if (this._environmentData !== null) {
                                    if (descriptor.isNew) {
                                        this._callbacks.markEnvironmentCodeAsSeen(this._environmentData, descriptor.code);
                                        descriptor.isNew = false; // update already loaded data
                                        isNewMark.isVisible = false; // update view
                                    }
                                    // Save new selected descriptor code as currently selected environment:
                                    await this._callbacks.saveLobbyEnvironmentAsync(this._environmentData, descriptor);
                                }
                            }
                        }
                        finally {
                            AppView.getInstance().fadingHelper?.startFadeIn(false);
                        }
                    }
                }
                finally {
                    loadingMark.isVisible = false;
                    if (!currentEnvironmentMark.isVisible)
                        loadableEnvironmentMark.isVisible = true;
                    this._changingEnvironment = false;
                }
            });
        }
        this._scrollViewer.valueToScrollTo = 0;
    }
    get isVisible() {
        return this._mesh2D.mesh.isVisible;
    }
}
